import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines, SectionLabel } from "./Reveal";
import { useLang } from "../i18n";

const ParallaxFrame = ({ src, alt, className, speed = 60 }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-speed, speed]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img src={src} alt={alt} style={{ y, scale: 1.25 }} className="absolute inset-0 w-full h-full object-cover" />
    </div>
  );
};

export const TourSection = ({ id, data, images, reverse }) => {
  const { lang, wa: waBase } = useLang();
  const wa = `${waBase}?text=${encodeURIComponent(`TSM WAY — ${data.label}`)}`;
  return (
    <section id={id} className="relative py-16 md:py-40 bg-[#0B0A08]" data-testid={`${id}-section`}>
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
          <div className="lg:sticky lg:top-32">
            <Reveal><SectionLabel index={data.index}>{data.label}</SectionLabel></Reveal>
            <h2 key={lang} className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#F7F4EE]">
              <MaskLines lines={[data.title]} />
            </h2>
            <Reveal delay={0.1}><p className="mt-8 text-sm sm:text-base text-[#CFC6B6] leading-relaxed max-w-md">{data.text}</p></Reveal>
            <Reveal className="lg:hidden mt-8"><ParallaxFrame src={images[0]} alt={data.label} className="aspect-[4/5] sm:aspect-[16/11] clip-frame" speed={30} /></Reveal>
            {data.route && (
              <Reveal delay={0.15}>
                <div className="mt-8" data-testid={`${id}-route`}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#D4AF37]">{data.routeLabel}</p>
                  <p className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-[#E8E1D3]">
                    {data.route.map((stop, i) => (
                      <span key={i} className="flex items-center gap-2">
                        {i > 0 && <span className="w-4 h-px bg-[#D4AF37]/50" />}
                        <span className={i === 0 || i === data.route.length - 1 ? "text-[#F3E5AB]" : ""}>{stop}</span>
                      </span>
                    ))}
                  </p>
                </div>
              </Reveal>
            )}
            <div className="mt-12 divide-y divide-[#D4AF37]/15 border-y border-[#D4AF37]/15">
              {data.items.map(([h, p], i) => (
                <Reveal key={h} delay={0.1 + i * 0.08}>
                  <div className="group py-6 flex gap-6" data-testid={`${id}-item-${i}`}>
                    <span className="font-mono text-xs text-[#D4AF37] pt-1.5">0{i + 1}</span>
                    <div>
                      <h3 className="font-display text-2xl text-[#F7F4EE] transition-colors duration-300 group-hover:text-[#F3E5AB]">{h}</h3>
                      <p className="mt-2 text-sm text-[#B5AC9C] leading-relaxed">{p}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <a href={wa} target="_blank" rel="noreferrer" className="btn-gold mt-10" data-testid={`${id}-cta-btn`}>
                {data.tag} <ArrowUpRight size={16} />
              </a>
            </Reveal>
          </div>
        </div>
        <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
          <div className="grid grid-cols-6 gap-4 md:gap-6">
            <Reveal className="hidden lg:block col-span-6"><ParallaxFrame src={images[0]} alt={data.label} className="aspect-[16/11] clip-frame" /></Reveal>
            <Reveal className="col-span-3 sm:mt-24" delay={0.1}><ParallaxFrame src={images[1]} alt={data.label} className="aspect-[4/5] rounded-t-full" speed={40} /></Reveal>
            <Reveal className="col-span-3" delay={0.2}>
              <ParallaxFrame src={images[2]} alt={data.label} className="aspect-[4/5]" speed={50} />
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.25em] text-[#E6C594]/80">{data.index} — {data.label}</p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
